'use client';

import { forwardRef, useMemo } from 'react';

import { css, cx } from '@/styled-system/css';

import { TableRowContext } from './context/table-row.context';
import { useAlignedCells } from './hooks/use-aligned-cells';
import { tableRowRecipe } from './styles/table-row.styles';
import type { TableRowPropsInterface } from './types/table-row.types';

/**
 * A native [`<th>`](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/tr) element.
 *
 * @since 8.0.0
 */
export const TableRow = forwardRef<HTMLTableRowElement, TableRowPropsInterface>(
  (props: TableRowPropsInterface, forwardedRef) => {
    const {
      'aria-selected': ariaSelected,
      children,
      className,
      css: cssProp,
      interactive = false,
      ...rest
    } = props;

    const styles = css(tableRowRecipe.raw({ interactive, selected: !!ariaSelected }), cssProp);

    const content = useAlignedCells({ children });

    const contextValue = useMemo(
      () => ({ interactive, selected: !!ariaSelected }),
      [interactive, ariaSelected],
    );

    return (
      <TableRowContext.Provider value={contextValue}>
        <tr
          ref={forwardedRef}
          aria-selected={ariaSelected}
          data-c11n-version={__C11N_VERSION__}
          data-c11n-component="Table.Row"
          className={cx('c11n', 'c11n_table__row', styles, className)}
          {...rest}
        >
          {content}
        </tr>
      </TableRowContext.Provider>
    );
  },
);

TableRow.displayName = 'Table.Row';
